import { useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import {ModalConfirmAction} from "components/modals/ModalConfirmAction"
import {SearchInput} from "components/search/SearchInput"
import {Table} from "components/tables/Table"

import { useProps } from "contexts/PropsContext"

import { SortOrderType } from "constants/eums"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"
import { AdminBonus2 } from "services/bonus/type"
import { createBonusAccess } from "services/user/type"

import { archivedBonus2TableColumns, bonus2TableColumns } from "../../constants/bonus2-columns"
import { buildDuplicateSeed, getDisplayName } from "../../helpers/bonus2.helpers"

import { BonusForm } from "./BonusForm"

interface Props {
  tenant: string
  /**
   * SOFT-644 — render the Archive partition instead of the live catalog. Same table, same
   * handler; the backend switches partition on the `archived` filter, so the two views can
   * never disagree about which side a template belongs to.
   */
  archived?: boolean
}

export const BonusListTable = ({ tenant, archived = false }: Props) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { user } = useProps()
  const [query, setQuery] = useState<string>("")
  const [editBonus, setEditBonus] = useState<AdminBonus2 | undefined>(undefined)
  const [duplicateBonus, setDuplicateBonus] = useState<AdminBonus2 | undefined>(undefined)
  const [showForm, setShowForm] = useState(false)
  const [tableUpdatedAt, setTableUpdatedAt] = useState<number>(Date.now())
  const [deleteTarget, setDeleteTarget] = useState<AdminBonus2 | null>(null)
  const [reactivateTarget, setReactivateTarget] = useState<AdminBonus2 | null>(null)
  const [archiveTarget, setArchiveTarget] = useState<AdminBonus2 | null>(null)
  const [unarchiveTarget, setUnarchiveTarget] = useState<AdminBonus2 | null>(null)

  // The column set stores i18n KEYS; Table renders `column.title` verbatim, so they are
  // resolved here (and re-resolved when the operator switches language).
  const columns = useMemo(
    () => (archived ? archivedBonus2TableColumns : bonus2TableColumns).map((c) => ({ ...c, title: t(c.title) })),
    [t, archived]
  )

  const handleCreateBonus = () => {
    setEditBonus(undefined)
    setDuplicateBonus(undefined)
    setShowForm(true)
  }

  const handleEditBonus = (bonus: AdminBonus2) => {
    setDuplicateBonus(undefined)
    setEditBonus(bonus)
    setShowForm(true)
  }

  // Opens the form pre-populated with the source's configuration but in CREATE mode, rather
  // than writing a copy straight to the backend. The two fields a clone must not inherit
  // (promo code, active) are cleared by `buildDuplicateSeed`, and both are things the operator
  // has to decide before the copy is worth saving — so the form is where this has to land.
  const handleDuplicateBonus = (bonus: AdminBonus2) => {
    setEditBonus(undefined)
    setDuplicateBonus(buildDuplicateSeed(bonus, t("bonus2.config.copySuffix") as string, t("bonus2.shared.unknownBonus") as string))
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditBonus(undefined)
    setDuplicateBonus(undefined)
  }

  const handleSaveBonus = () => {
    setTableUpdatedAt(Date.now())
  }

  const handleDelete = async (bonus: AdminBonus2) => {
    const result = await AdminBonus2Service.deleteBonus(bonus.id, tenant)
    if (result.ok) {
      toast.success(t("bonus2.config.toastDeleted") as string)
      setTableUpdatedAt(Date.now())
      return
    }
    // SOFT-644 — the refusal the operator can actually resolve. Deleting is impossible for
    // a template with player history, so send them to Archive instead of leaving them
    // retrying a button that can never succeed.
    if (result.inUse) {
      toast.error(t("bonus2.config.toastDeleteInUse") as string)
      return
    }
    toast.error(result.message || (t("bonus2.config.toastDeleteFailed") as string))
  }

  const handleReactivate = async (bonus: AdminBonus2) => {
    const result = await AdminBonus2Service.reactivateBonus(bonus.id, tenant)
    if (result) {
      toast.success(t("bonus2.config.toastReactivated") as string)
      setTableUpdatedAt(Date.now())
    } else {
      toast.error(t("bonus2.config.toastReactivateFailed") as string)
    }
  }

  const handleArchive = async (bonus: AdminBonus2) => {
    const success = await AdminBonus2Service.archiveBonus(bonus.id, tenant)
    if (success) {
      toast.success(t("bonus2.config.toastArchived") as string)
      setTableUpdatedAt(Date.now())
    } else {
      toast.error(t("bonus2.config.toastArchiveFailed") as string)
    }
  }

  const handleUnarchive = async (bonus: AdminBonus2) => {
    const success = await AdminBonus2Service.unarchiveBonus(bonus.id, tenant)
    if (success) {
      toast.success(t("bonus2.config.toastUnarchived") as string)
      setTableUpdatedAt(Date.now())
    } else {
      toast.error(t("bonus2.config.toastUnarchiveFailed") as string)
    }
  }

  // Offered in BOTH tabs: an archived template is exactly the kind an operator wants to
  // revive as a new variant, and the clone lands on the main list either way (it saves as a
  // fresh, unarchived, inactive template).
  const duplicateAction = {
    name: t("bonus2.config.duplicate") as string,
    handler: handleDuplicateBonus,
    buttonColor: "bg-sky-600 hover:bg-sky-700"
  }

  // Offered in BOTH tabs: Delete is the only way to get rid of a template nobody ever claimed
  // (a typo, a test row) — archiving those would just fill the Archive tab with junk — and the
  // Archive tab is where an operator tidies up. For anything with player history the backend
  // refuses and `handleDelete` points them at Archive instead.
  //
  // NOTE: this used to be labelled "Deactivate" while calling DELETE, so on every template a
  // player had ever claimed it failed with advice ("deactivate instead") the button could not
  // carry out. Real deactivation is the "Active" toggle in the bonus form.
  const deleteAction = {
    name: t("bonus2.config.delete") as string,
    handler: (row: AdminBonus2) => setDeleteTarget(row),
    buttonColor: "bg-rose-600 hover:bg-rose-700"
  }

  // Archiving leaves Active/Inactive alone, so an archived template keeps whatever lifecycle
  // state it had. The Archive tab still offers no Reactivate: flipping the active flag of a
  // retired template is a decision to make once it is back on the main list.
  const actions = archived
    ? [
        {
          name: t("common.edit") as string,
          handler: handleEditBonus
        },
        duplicateAction,
        {
          name: t("bonus2.config.unarchive") as string,
          handler: (row: AdminBonus2) => setUnarchiveTarget(row),
          buttonColor: "bg-emerald-600 hover:bg-emerald-700"
        },
        deleteAction
      ]
    : [
        {
          name: t("common.edit") as string,
          handler: handleEditBonus
        },
        duplicateAction,
        {
          name: t("bonus2.config.reactivate") as string,
          handler: (row: AdminBonus2) => setReactivateTarget(row),
          condition: (row: AdminBonus2) => !row.active,
          buttonColor: "bg-emerald-600 hover:bg-emerald-700"
        },
        // Offered for ACTIVE templates too: archiving is the only way to retire one that
        // player bonuses reference, and Delete is refused for exactly those. Archiving does
        // NOT deactivate — players mid-funnel are untouched.
        {
          name: t("bonus2.config.archive") as string,
          handler: (row: AdminBonus2) => setArchiveTarget(row),
          buttonColor: "bg-slate-600 hover:bg-slate-700"
        },
        deleteAction
      ]

  return (
    <>
      <div className="flex items-end">
        <div className="flex flex-1 space-x-4 max-w-md">
          <SearchInput placeholder={t("bonus2.config.searchByTitle")} onChange={setQuery} />
        </div>
        {!archived && user && createBonusAccess.includes(user.role) ? (
          <button
            className="ml-auto btn btn-primary bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent dark:text-white hover:text-white"
            onClick={handleCreateBonus}
          >
            {t("bonus2.config.create")}
          </button>
        ) : null}
      </div>

      <Table
        title={t(archived ? "bonus2.config.archivedBonusTemplates" : "bonus2.config.bonusTemplates")}
        handler={AdminBonus2Service.getBonusesTable}
        columns={columns}
        actions={actions}
        updatedAt={tableUpdatedAt}
        onRowClick={handleEditBonus}
        filters={{ tenant, q: query, ...(archived && { archived: "true" }) }}
        defaultSortOrder={SortOrderType.ASC}
        defaultSortOrderBy="priority"
      />

      {deleteTarget && (
        <ModalConfirmAction
          data={deleteTarget}
          handler={() => setDeleteTarget(null)}
          text={
            t("bonus2.config.confirmDelete", {
              name: getDisplayName(deleteTarget.name, t("bonus2.shared.unknownBonus") as string)
            }) as string
          }
          actionText={t("bonus2.config.delete") as string}
          actionHandler={async (target: AdminBonus2) => {
            await handleDelete(target)
            setDeleteTarget(null)
          }}
        />
      )}

      {reactivateTarget && (
        <ModalConfirmAction
          data={reactivateTarget}
          handler={() => setReactivateTarget(null)}
          text={t("bonus2.config.confirmReactivate", { name: getDisplayName(reactivateTarget.name, t("bonus2.shared.unknownBonus") as string) }) as string}
          actionText={t("bonus2.config.reactivate") as string}
          actionHandler={async (target: AdminBonus2) => {
            await handleReactivate(target)
            setReactivateTarget(null)
          }}
        />
      )}

      {archiveTarget && (
        <ModalConfirmAction
          data={archiveTarget}
          handler={() => setArchiveTarget(null)}
          text={
            t("bonus2.config.confirmArchive", {
              name: getDisplayName(archiveTarget.name, t("bonus2.shared.unknownBonus") as string)
            }) as string
          }
          actionText={t("bonus2.config.archive") as string}
          actionHandler={async (target: AdminBonus2) => {
            await handleArchive(target)
            setArchiveTarget(null)
          }}
        />
      )}

      {unarchiveTarget && (
        <ModalConfirmAction
          data={unarchiveTarget}
          handler={() => setUnarchiveTarget(null)}
          text={
            t("bonus2.config.confirmUnarchive", {
              name: getDisplayName(unarchiveTarget.name, t("bonus2.shared.unknownBonus") as string)
            }) as string
          }
          actionText={t("bonus2.config.unarchive") as string}
          actionHandler={async (target: AdminBonus2) => {
            await handleUnarchive(target)
            setUnarchiveTarget(null)
          }}
        />
      )}

      {showForm && (
        <BonusForm
          bonus={editBonus}
          duplicateFrom={duplicateBonus}
          tenant={tenant}
          onClose={handleCloseForm}
          onSave={handleSaveBonus}
        />
      )}
    </>
  )
}

/**
 * SOFT-644 — the Archive tab. A thin wrapper so the tab registry can stay a plain
 * `Record<BonusTabType, FC<{ tenant: string }>>`.
 */
export const ArchivedBonusListTable = ({ tenant }: { tenant: string }) => <BonusListTable tenant={tenant} archived />
